import { useState, MouseEvent, useEffect } from "react";
import adminClasses from "../../../landing-pages/admin/admin.module.css";
import IdNameModel from "../../../../models/common/IdNameModel";
import RegisteredInterviewerModel from "../../../../models/admin/interviewer/response/RegisteredInterviewerModel";
import PagedListModel from "../../../../models/common/PagedListModel";
import RegisteredInterviewerSearchInputModel from "../../../../models/admin/interviewer/request/RegisteredInterviewerSearchInputModel";
import { SortDirection } from "../../../../models/enums/SortDirectionEnum";
import modalUtils from "../../../../utils/modal-utils";
import loaderUtils from "../../../../utils/loader-utils";
import adminInterviewerService from "../../../../services/admin/admin-interviewer-service";
import toasterUtils from "../../../../utils/toaster-utils";
import RegisteredInterviewersGridComponent from "./RegisteredInterviewersGridComponent";
import { PagingEnum } from "../../../../models/enums/IAIEnum";
import masterDataService from "../../../../services/master-data-service";
import { Autocomplete, Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

interface RegisteredInterviewersProps {
    primarySkillsList: IdNameModel[];
};

const RegisteredInterviewersComponent = (props: RegisteredInterviewersProps) => {
    const [interviewerPagedList, setInterviewerPagedList] = useState<PagedListModel<RegisteredInterviewerModel>>();
    const [interviewerList, setInterviewerList] = useState<RegisteredInterviewerModel[]>([]);
    const [refreshInterviewer, setRefreshInterviewer] = useState<number>(2);
    const [primarySKillSearch, setPrimarySkillSearch] = useState<number>(0);
    const [nameFilter, setNameFilter] = useState<string>("");
    const [emailIdFilter, setEmailIdFilter] = useState<string>("");
    const [page, setPage] = useState<number>(PagingEnum.page);
    const [pageSize, setPageSize] = useState<number>(PagingEnum.pageSize);
    const [sortColumn, setSortColumn] = useState<string>("Name");
    const [sortDirection, setSortDirection] = useState<number>(PagingEnum.sortDirection);
    const [pageSizesList, setPageSizesList] = useState<number[]>([]);

    useEffect(() => {
        loadPageSizes();
    }, []);

    const loadPageSizes = () => {
        masterDataService.loadPageSizesForPaging().then((res) => {
            setPageSizesList(res);
        }).catch((error) => {
            toasterUtils.showError(error);
        });
    };

    const getSearchFilters = (nameFilter: string, emailIdFilter: string, page: number, pageSize: number, sortColumn: string, sortDirection: number) => {
        var inputData: RegisteredInterviewerSearchInputModel = {
            primarySkillId: primarySKillSearch,
            name: nameFilter,
            emailId: emailIdFilter,
            page: page,
            pageSize: pageSize,
            sort: sortColumn,
            sortDir: sortDirection
        };
        return inputData;
    };

    const getRegisteredInterviewers = (inputData: RegisteredInterviewerSearchInputModel) => {
        if (primarySKillSearch == 0) {
            toasterUtils.showWarning("Please select Primary Skill.");
            return;
        }
        setRefreshInterviewer(2);
        modalUtils.showLoader();
        adminInterviewerService.getRegisteredInterviewers(inputData).then((res) => {
            modalUtils.closeModal();
            setInterviewerList([]);
            setInterviewerPagedList(res);
            setInterviewerList(res.items);
            setRefreshInterviewer(refreshInterviewer * 5);
        }).catch((error) => {
            setRefreshInterviewer(refreshInterviewer * 5);
            modalUtils.closeModal();
            toasterUtils.showError(error);
        });
    };

    const onApproveActionHandler = (interviewerId: string) => {
        modalUtils.showLoader();
        adminInterviewerService.approveInterviewer(interviewerId).then((res) => {
            modalUtils.closeModal();
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            toasterUtils.showSuccess(res.informationMessages.toString());
            getRegisteredInterviewers(getSearchFilters(nameFilter, emailIdFilter, page, pageSize, sortColumn, sortDirection));
        }).catch((error) => {
            modalUtils.closeModal();
            toasterUtils.showError(error);
        });
    };

    const onPageChangeActionHandler = (newPage: number) => {
        setPage(newPage);
        getRegisteredInterviewers(getSearchFilters(nameFilter, emailIdFilter, newPage, pageSize, sortColumn, sortDirection));
    };

    const onPageSizeChangeActionHandler = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(PagingEnum.page);
        getRegisteredInterviewers(getSearchFilters(nameFilter, emailIdFilter, PagingEnum.page, newPageSize, sortColumn, sortDirection));
    };

    const onSortActionHandler = (newSortColumn: string, sortDir: string) => {
        setSortColumn(newSortColumn);
        setSortDirection(sortDir === "asc" ? SortDirection.Asc : SortDirection.Desc);
        getRegisteredInterviewers(getSearchFilters(nameFilter, emailIdFilter, page, pageSize, newSortColumn, sortDir === "asc" ? SortDirection.Asc : SortDirection.Desc));
    };

    const onFilterActionHandler = (filterColumn: string, filterValue: string) => {
        setPage(0);
        if (filterColumn === "Name") {
            setNameFilter(filterValue);
            getRegisteredInterviewers(getSearchFilters(filterValue, emailIdFilter, 0, pageSize, sortColumn, sortDirection));
        } else {
            setEmailIdFilter(filterValue);
            getRegisteredInterviewers(getSearchFilters(nameFilter, filterValue, 0, pageSize, sortColumn, sortDirection));
        }
    };


    return (
        <div className="card card-white">
            <div className="card-body">
                <div className="row">
                    <div className="col-md-3">
                        <FormControl fullWidth margin="normal" size="small">
                            <Autocomplete options={props.primarySkillsList} value={props.primarySkillsList.find((x) => x.id === primarySKillSearch) || null}
                                onChange={(_, newValue) => setPrimarySkillSearch(newValue ? newValue.id : 0)} getOptionLabel={(option) => option?.name || ""} isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderInput={(params) => (
                                    <TextField {...params} label="Primary Skill" size="small" margin="normal" fullWidth required />
                                )}
                            />
                        </FormControl>
                    </div>
                    <div className="col-md-3 align-vertical-center">
                        <Button variant="contained" color="success" onClick={() => getRegisteredInterviewers(getSearchFilters(nameFilter, emailIdFilter, page, pageSize, sortColumn, sortDirection))} startIcon={<SearchIcon />} sx={{ ml: 2, mt: 2 }}>
                            Search
                        </Button>
                    </div>
                </div>
                {interviewerList.length > 0 && refreshInterviewer > 2 &&
                    <RegisteredInterviewersGridComponent
                        key={refreshInterviewer}
                        interviewersList={interviewerList}
                        totalSize={interviewerPagedList?.totalCount ? interviewerPagedList?.totalCount : interviewerList.length}
                        currentPage={page + 1}
                        pagesize={pageSize}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection === SortDirection.Asc ? "asc" : "desc"}
                        pageSizesList={pageSizesList}
                        nameFilterValue={nameFilter}
                        emailIdFilterValue={emailIdFilter}
                        onApproveAction={onApproveActionHandler}
                        onPageChangeAction={onPageChangeActionHandler}
                        onPageSizeChangeAction={onPageSizeChangeActionHandler}
                        onSortAction={onSortActionHandler}
                        onFilterAction={onFilterActionHandler} />
                }
            </div>
        </div>
    );
};
export default RegisteredInterviewersComponent;